// components/OrigenCompra.js — pedidos-origen NIVEL 1 (30-sep-2026). El bloque «Origen de la
// compra» de la ficha del pedido: fetch propio a GET /api/orders/:id/origen (lee SOLO el
// espejo) y TRADUCCIÓN solo acá — el payload viaja con los crudos. Reglas del diseño
// aprobado: un valor desconocido se muestra CRUDO (el riesgo es ocultar); «sin atribución»
// declarado en tres sabores (POS / a-mano / web sin datos); el error de consulta se distingue
// de «sin atribución» (fuente caída ≠ no hay dato). Es información DEL PEDIDO: no alimenta
// Analítica ni Finanzas. Líneas simples apiladas (como AddrBlock) — se leen igual a 390 px.
//
// DOS superficies lo montan (la lección del 30-sep: el bloque se cableó primero SOLO en el
// modal viejo, que hoy abre Caja, y Pedidos usa el DRAWER — la ficha real quedó sin bloque):
//   · variante="modal"  → OrderDetailModal (Caja), clases odm-*;
//   · variante="drawer" → OrderInspectorDrawer (Pedidos/Resumen/Caja lectura), clases op-*.
// El cuerpo (fetch, sabores, traducciones) es UNO; solo cambia el cascarón.
import React, { useState, useEffect } from 'react';
import { colDateTimeStr } from '../utils/colTimeUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Traducciones de pantalla sobre los CRUDOS medidos en el censo. Un valor que no esté acá
// se muestra tal cual — jamás se aplana a «Otro».
export const TIPOS_ORIGEN = {
  typein: 'Directo (escribió la dirección)',
  organic: 'Búsqueda',
  referral: 'Vino de otro sitio',
  utm: 'Campaña',
};
export const FUENTES = {
  ig: 'Instagram', instagram: 'Instagram', 'l.instagram.com': 'Instagram',
  google: 'Google', facebook: 'Facebook', 'm.facebook.com': 'Facebook',
  'eventsmanager.facebook.com': 'Facebook', meta_ads: 'Meta Ads',
};
export const DISPOSITIVOS = { Mobile: 'celular', Desktop: 'computador', Tablet: 'tableta' };

export const tipoOrigenTexto = (t) => TIPOS_ORIGEN[t] || t;
export const dispositivoTexto = (d) => DISPOSITIVOS[d] || d;
// '(direct)' no es una fuente que decirle al dueño (el tipo «Directo» ya lo dice). El nombre
// lindo lleva el crudo entre paréntesis salvo que sean la misma palabra («Google (google)» no).
export const fuenteTexto = (f) => {
  if (!f || f === '(direct)') return null;
  const lindo = FUENTES[f];
  if (!lindo) return f;
  return lindo.toLowerCase() === String(f).toLowerCase() ? lindo : `${lindo} (${f})`;
};

// session_start_time viene en UTC NAIVE ('YYYY-MM-DD HH:MM:SS' sin zona): lo escribe
// sourcebuster en el navegador en UTC, al revés de date_created del pedido (hora de la
// tienda). Verificado contra el espejo (30-sep): pedido #11465 creado 20-sep 23:57 COT con
// visita «2026-09-21 04:55:58» = 23:55 COT, 1,5 min antes — leído como local mostraba la
// visita 5 h DESPUÉS de la compra. Se interpreta UTC y se muestra en hora de la tienda vía
// colDateTimeStr (el offset vive UNA vez, en colTimeUtils). No parseable → crudo.
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
export const fmtInicioVisita = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(String(s || '').trim());
  if (!m) return s || null;
  const col = colDateTimeStr(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] || '00'}Z`); // UTC → COT
  if (!col) return s;
  const [y, mes, d] = col.slice(0, 10).split('-').map(Number);
  const [h, min] = col.slice(11).split(':').map(Number);
  const h12 = ((h + 11) % 12) + 1;
  return `${d} ${MESES_CORTOS[mes - 1]} ${y}, ${h12}:${String(min).padStart(2, '0')} ${h < 12 ? 'a. m.' : 'p. m.'}`;
};

function Linea({ rotulo, children }) {
  return <div className="odm-addr-line"><b>{rotulo}:</b> {children}</div>;
}

export default function OrigenCompra({ orderId, variante = 'modal' }) {
  const [st, setSt] = useState({ estado: 'cargando' });
  useEffect(() => {
    if (!orderId) return undefined;
    let cancelled = false;
    setSt({ estado: 'cargando' });
    fetch(`${API_URL}/api/orders/${orderId}/origen`)
      .then((res) => res.json().then((data) => ({ res, data })))
      .then(({ res, data }) => {
        if (cancelled) return;
        if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
        setSt({ estado: 'ok', data });
      })
      .catch(() => { if (!cancelled) setSt({ estado: 'error' }); });
    return () => { cancelled = true; };
  }, [orderId]);

  let cuerpo;
  if (st.estado === 'cargando') {
    cuerpo = <div className="odm-addr-line">Cargando…</div>;
  } else if (st.estado === 'error') {
    // Fuente caída ≠ «sin atribución»: no se afirma nada sobre el pedido.
    cuerpo = <div className="odm-addr-line">No se pudo consultar el origen de este pedido.</div>;
  } else {
    const o = st.data || {};
    if (o.clase === 'pos') {
      cuerpo = <div className="odm-addr-line">Venta presencial (POS): sin recorrido web.</div>;
    } else if (o.clase === 'manual') {
      cuerpo = <div className="odm-addr-line">Pedido creado a mano en la tienda (wp-admin): sin recorrido web.</div>;
    } else if (o.clase === 'web') {
      const fuente = fuenteTexto(o.fuente);
      cuerpo = (
        <>
          <Linea rotulo="Llegó por">
            {tipoOrigenTexto(o.tipo_origen)}{fuente ? ` · ${fuente}` : ''}
            {o.campania ? ` · campaña: ${o.campania}` : ''}
            {o.contenido ? ` · enlace: ${o.contenido}` : ''}
          </Linea>
          {o.referido_de && <Linea rotulo="Venía de">{o.referido_de}</Linea>}
          {o.dispositivo && <Linea rotulo="Dispositivo">{dispositivoTexto(o.dispositivo)}</Linea>}
          {o.ciudad && <Linea rotulo="Ciudad">{o.ciudad}</Linea>}
          {o.entrada && <Linea rotulo="Entró por">{o.entrada}</Linea>}
          {o.paginas_sesion != null && (
            <Linea rotulo="En su visita">
              {`vio ${o.paginas_sesion} ${o.paginas_sesion === 1 ? 'página' : 'páginas'}`}
              {o.numero_visita != null ? ` (visita número ${o.numero_visita})` : ''}
            </Linea>
          )}
          {o.inicio_visita && <Linea rotulo="La visita empezó">{fmtInicioVisita(o.inicio_visita)}</Linea>}
          {o.minutos_hasta_compra != null && (
            <Linea rotulo="Hasta comprar">
              {o.minutos_hasta_compra === 0 ? 'menos de 1 minuto' : `${o.minutos_hasta_compra} ${o.minutos_hasta_compra === 1 ? 'minuto' : 'minutos'}`}
            </Linea>
          )}
        </>
      );
    } else {
      cuerpo = (
        <div className="odm-addr-line">
          Este pedido no trae datos de origen: es anterior a la captura o el comprador no aceptó el rastreo.
        </div>
      );
    }
  }

  if (variante === 'drawer') {
    return (
      <>
        <h4 className="op-insp-h">Origen de la compra</h4>
        <div className="op-origen">{cuerpo}</div>
      </>
    );
  }
  return (
    <div className="odm-section">
      <div className="odm-section-title">Origen de la compra</div>
      <div className="odm-addr-block">{cuerpo}</div>
    </div>
  );
}
